import { SortingState } from '@tanstack/react-table';
import { useQuery } from 'react-query';
import { NarrativeRequest } from 'utilitys/narrative/narrativeTools';

import { getResourceTable as queryFn } from 'api/endPoints/getResourceTable';
import { IVersion } from 'api/queries/getProjectVersionsList.query';
import { QueryKeys } from 'api/queryKeys';

import { Range } from 'components/common/GlobalFiltersInPage/components/RangeFilter/rangeFilter';
import { QuickAnswerFilter } from 'components/common/GlobalFiltersInPage/components/quickAnswerFilter/quickAnswerFilter';
import { ICardLastComment } from 'components/common/Tables/Cells/DetailsCell/DetailsCell';
import { Pagination } from 'components/common/pagination/customPagination';

import {
    IAssignee,
    ICardIndicators,
    ICategory,
    IStatus,
    ITagList,
} from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/battlecards.types';
import { CardType } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/helper';
import { IFilter } from 'components/Dashboards/Project/Components/CustomDashboard/utils/utils.type';
import { Aggregation } from 'components/widgets/ResourcesAnalysisChart/ResourcesAnalysisChart.consts';

export const useQueryGetResourceTable = (params: QueryParams, endpoint?: string) => {
    return useQuery<QueryResponse, QueryError, QueryResponse, [QueryKeys, unknown]>({
        queryKey: ['getResourceTable', params],
        queryFn: () => queryFn(params, endpoint),
        enabled: Boolean(params.metaDataId) && Boolean(params.comparedMetaDataId),
        cacheTime: 0,
    });
};

export interface QueryParams extends NarrativeRequest {
    metaDataId: IVersion['id'] | null;
    comparedMetaDataId: IVersion['id'] | null;
    range: Range | undefined;
    filter: {
        filters: IFilter[];
    };
    sortingRequestList: SortingState;
    paging: Pagination;
    barId: string | null;
    aggregation: Aggregation;
    quickAnswerFilter?: QuickAnswerFilter;
    startDate: number | undefined;
    endDate: number | undefined;
}

export interface QueryResponse {
    cardList: {
        id: number;
        metaDataId: number;
        criticalityScore: number;
        float: number;
        taskHashCode: string;
        taskVersionHashCode: string;
        plannedStartDate: number;
        plannedFinishDate: number;
        actualStartDate: number;
        actualFinishDate: number | null;
        numOfPredecessors: number;
        numOfSuccessors: number;
        projectWeekStart: number;
        projectWeekFinish: number;
        projectMonthStart: number;
        projectMonthFinish: number;
        status: IStatus;
        originalStatus: IStatus;
        taskActivityId: string;
        parentActivity: string;
        grandParentActivity: string;
        plannedDurationInDays: number;
        actualDurationInDays: number;
        remainingDurationInDays: number;
        completedDurationInDays: number;
        taskActivityType: string;
        isVirtual: boolean;
        category: ICategory;
        title: string;
        startDate: number;
        endDate: number;
        dueDate: number | null;
        duration: number;
        rank: number;
        tagList: ITagList[];
        versionDate: number;
        isStartOverdue: boolean;
        isFinishOverdue: boolean;
        taskId: number;
        startVariance: number;
        finishVariance: number;
        currentDurationOverrun: number;
        completedDurationOverrun: number;
        isInterimChanged: boolean;
        overrunPercent: number;
        assignee: IAssignee | null;
        details: string;
        cardLastComment: ICardLastComment;
        cardIndicators: ICardIndicators;
        submittedDurationComplete: number;
        isTracked: boolean;
        precedingActivities: number;
        succeedingActivites: number;
        precedingRisk: string;
        succeedingRisk: string;
        type: {
            id: number;
            name: CardType;
            label: string;
        };
        baselineStartDate: number;
        baselineFinishDate: number;
        orderInPage: number;
        uniqId: number;
        allBudgetResourceUnit: null;
        allActualResourceUnit: null;
        allResourceNameList: string[];
        actualResourceUnit: number;
        remainingResourceUnit: number;
        resourceName: string;
        resourceType: string;
        comparedBudgetResourceUnit: number;
        isGenAiDetails: boolean;
    }[];
    numOfAllCards: number;
    numOfPages: number;
    info: IToolTip;
}
